import { useMemo, useState } from "react";
import {
  ArrowLeft,
  CreditCard,
  ExternalLink,
  HardHat,
  Package,
  Printer,
  Shield,
  ShieldAlert,
  UserCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { AppShell } from "@/admin/components/app-shell";
import { ConfigHelp } from "@/admin/components/config-help";
import { ICON_MAP, DEFAULT_ICON_KEY } from "@/admin/components/category-icons";
import { DetailCard, FieldRow } from "@/admin/components/shared/detail-card";
import { PartsOnUnitCard } from "@/admin/components/registrations/parts-on-unit-card";
import { PartnerProfilePanel } from "@/admin/components/shared/partner-profile-panel";
import {
  defineField,
  formatDisplayValue,
  formatPhoneDisplay,
  isDisplayEmpty,
  type FieldType,
} from "@/shared/lib/forms";
import { humanise } from "@/admin/lib/display-helpers";
import { NotesPanel } from "@/admin/components/shared/notes-panel";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { useHolding } from "@/admin/hooks/use-products";
import { usePartnershipDetail } from "@/admin/hooks/use-partnerships";
import { useEntityTypeByKey, useEntityTypes } from "@/admin/hooks/use-entities";
import { useFormatting } from "@/admin/hooks/use-formatting";
import { useOrg } from "@/admin/hooks/use-org";
import { FLAGS, useFeatureFlag } from "@/admin/hooks/use-feature-flag";
import { TierBadge } from "@/shared/components/ui/tier-badge";
import { useCertificateDownloadEnabled } from "@/admin/hooks/use-registration-settings";
import type {
  Holding,
  HoldingWarranty,
  PendingWarrantySummary,
} from "@/admin/types/products";
import { manufacturerWarrantyRecord } from "@/admin/lib/manufacturer-warranty";
import {
  registrationCardDisplayColumns,
  purchaseCardDisplayColumns,
} from "@/admin/lib/entity-card-display";
import { RegistrationConfiguredFieldRows } from "@/admin/components/registration-configured-fields";
import { CustomerActionControls } from "@/admin/components/shared/customer-action-controls";
import { SharedAddressBadge } from "@/admin/components/shared/address-matches";
import {
  DraggableCardGrid,
  type ReorderableCard,
} from "@/admin/components/shared/draggable-card-grid";
import { PinMyViewBar } from "@/admin/components/shared/pin-my-view";
import { usePinnedView } from "@/admin/hooks/use-pinned-view";
import { EntityActionMenu } from "@/admin/components/shared/entity-action-menu";
import {
  buildRegistrationConfiguredFieldItems,
  holdingRegistrationCardColumnApplies,
  type RegistrationConfiguredFieldItem,
} from "@/admin/lib/registration-configured-fields";
import { WorkflowIssueResolveModal } from "@/admin/components/workflow-issues/workflow-issue-resolve-modal";
import { useModal } from "@/shared/components/modals/modal-provider";
import { EntityHistoryCard } from "@/shared/components/workflows";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { useClaims } from "@/admin/hooks/use-claims";
import api from "@/admin/lib/api";
import { toastApiError } from "@/shared/lib/api-error";
import { computeWarrantyExpiresAt } from "@/shared/lib/warranty";
import {
  formatDurationFromMonths,
  formatHoldingId,
} from "@/admin/lib/formatting";

const WARRANTY_TYPE_LABELS: Record<string, string> = {
  MANUFACTURER_WARRANTY: "Standard",
  RETAILER_WARRANTY: "Retailer",
  EXTENDED_WARRANTY: "Extended",
};

const STATUS_WORKFLOW_KEY = "lifecycle_status";

type WarrantyRecordSource = HoldingWarranty | PendingWarrantySummary;

function isIssuedWarranty(
  warranty: WarrantyRecordSource,
): warranty is HoldingWarranty {
  return "uuid" in warranty;
}

function findWarranty(
  holding: Holding | undefined,
  warrantyUuid: string | undefined,
): WarrantyRecordSource | null {
  if (!holding || !warrantyUuid) return null;
  const firstIssued = holding.warranties?.[0] ?? null;
  if (warrantyUuid === `product-${holding.uuid}`) {
    return (
      firstIssued ??
      holding.pending_warranty_summary ??
      // No issued/pending warranty: surface the manufacturer-declared
      // coverage carried on the product record before falling back to
      // the bare placeholder.
      manufacturerWarrantyRecord(holding) ?? {
        option_display_name: "No warranty issued",
        class_name: holding.product_category || "Warranty",
        warranty_type: "PENDING_WARRANTY",
        coverage_scope: null,
        duration_in_months: null,
        is_lifetime: false,
        active_from: holding.created_at,
        status: holding.status,
      }
    );
  }
  return (
    holding.warranties?.find((warranty) => warranty.uuid === warrantyUuid) ??
    (warrantyUuid === "pending-warranty"
      ? (holding.pending_warranty_summary ?? firstIssued)
      : null)
  );
}

/**
 * When the router renders this page at ``/registrations/:uuid`` (vehicle
 * detail view), ``warrantyUuid`` is absent. Mirror ``buildWarrantyRows`` so
 * we resolve the same primary record as the property-style list would use.
 */
function defaultWarrantyUuidForHolding(holding: Holding): string {
  const firstIssued = holding.warranties?.[0]?.uuid;
  if (firstIssued) return firstIssued;
  if (holding.pending_warranty_summary) return "pending-warranty";
  return `product-${holding.uuid}`;
}

function durationLabel(warranty: WarrantyRecordSource): string {
  if (warranty.is_lifetime) return "Lifetime";
  return formatDurationFromMonths(warranty.duration_in_months) || "-";
}

function headerWarrantyTemplateLabel(
  warrantyRecord: WarrantyRecordSource,
): string {
  const name = warrantyRecord.warranty_template_name?.trim();
  if (name) return name;
  const fallback = warrantyRecord.class_name?.trim();
  if (fallback) return fallback;
  return "-";
}

function deriveRecordStatus(
  holdingStatus: string | undefined,
  warrantyStatus: string | undefined,
): string {
  if (holdingStatus === "cancelled") return "Cancelled";
  if (holdingStatus === "transferred") return "Transferred";
  return humanise(warrantyStatus) || humanise(holdingStatus) || "Active";
}

/** Read a host EAV value (host template field) for header display. */
function hostTemplateAttrText(
  host: Holding["host"] | null | undefined,
  codeName: string,
): string {
  if (!host?.attribute_values) return "";
  const entry = host.attribute_values.find((a) => a.code_name === codeName);
  if (entry == null || entry.value === null || entry.value === undefined) {
    return "";
  }
  if (typeof entry.value === "string") return entry.value.trim();
  if (typeof entry.value === "number" || typeof entry.value === "boolean") {
    return String(entry.value).trim();
  }
  return humanise(entry.value).trim();
}

export function RegistrationWarrantyRecordPage() {
  const cardsView = usePinnedView("registration-detail");
  const { uuid, warrantyUuid } = useParams<{
    uuid: string;
    warrantyUuid: string;
  }>();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { t } = useTranslation();
  const { orgUuid } = useOrg();
  const certificateDownloadEnabled = useCertificateDownloadEnabled();
  // Tier identity is a per-tenant capability. Off (the default) keeps the
  // plain text badge every tenant has today. Declared with the other
  // top-level hooks so it runs on every render, before any early return.
  const tiersEnabled = useFeatureFlag(FLAGS.TIERS);
  const { formatDate, formatCurrency, locale, currency } = useFormatting();
  const {
    data: holding,
    isLoading,
    isError,
    refetch,
  } = useHolding(uuid ?? null);
  // Full profile for the partner this registration was registered against.
  // The holding payload carries only the partner's name; the attribute values
  // the profile panel renders come from the partnership detail endpoint.
  const { data: assignedPartner } = usePartnershipDetail(
    holding?.partnership?.uuid ?? null,
  );
  const { data: hostEntity } = useEntityTypeByKey("host");
  const { data: holdingEntity } = useEntityTypeByKey("holding");
  const { data: warrantyEntity } = useEntityTypeByKey("warranty");
  const { data: consumerEntity } = useEntityTypeByKey("consumer");
  const { data: entityTypes = [] } = useEntityTypes();
  const [isDownloadingCertificate, setIsDownloadingCertificate] =
    useState(false);
  const claimsParams = useMemo(
    () => ({ holding_uuid: uuid ?? "", page_size: "2" }),
    [uuid],
  );
  const { data: claimsData } = useClaims(claimsParams);

  const effectiveWarrantyUuid = useMemo(() => {
    if (warrantyUuid) return warrantyUuid;
    if (!holding) return undefined;
    return defaultWarrantyUuidForHolding(holding);
  }, [holding, warrantyUuid]);

  const warranty = useMemo(
    () => findWarranty(holding, effectiveWarrantyUuid),
    [holding, effectiveWarrantyUuid],
  );

  // Resolve the warranty template uuid so the config-help "?" can deep-link to
  // the exact template's Display tab. Only issued warranties carry a template
  // reference; pending/placeholder rows have none, so the warranty tip is
  // dropped rather than pointing at the generic template list.
  const warrantyTemplateUuid = useMemo(
    () =>
      warranty && isIssuedWarranty(warranty)
        ? (warranty.warranty_template?.uuid ?? null)
        : null,
    [warranty],
  );

  if (isLoading) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            Loading warranty record...
          </p>
        </main>
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/registrations")}
            className="mb-5 h-auto gap-1 bg-transparent p-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to registrations
          </Button>
          <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-5 py-10 text-center">
            <p className="text-[13px] font-medium text-[var(--ink)]">
              Warranty record could not be loaded.
            </p>
            <p className="mt-1 text-[12px] text-[var(--ink-3)]">
              Refresh the page or return to the registration and try again.
            </p>
          </div>
        </main>
      </AppShell>
    );
  }

  if (!holding || !warranty) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/registrations")}
            className="mb-5 h-auto gap-1 bg-transparent p-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to registrations
          </Button>
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            Warranty record not found.
          </p>
        </main>
      </AppShell>
    );
  }

  const issuedWarranty = isIssuedWarranty(warranty) ? warranty : null;
  const pendingIssues = holding.pending_exceptions ?? [];
  const statusWorkflowState =
    holding.workflow_states?.find(
      (workflowState) => workflowState.workflow_key === STATUS_WORKFLOW_KEY,
    ) ?? null;
  const holdingStatusKey = holding.status?.toLowerCase() ?? null;
  const statusWorkflowStateKey =
    statusWorkflowState?.state_key?.toLowerCase() ?? null;
  const hasStatusWorkflowIssues = pendingIssues.some((issue) => {
    if (issue.workflow_key === STATUS_WORKFLOW_KEY) return true;
    if (issue.workflow_key && issue.workflow_key !== STATUS_WORKFLOW_KEY) {
      return false;
    }
    return issue.issue_kind === "approval_required";
  });
  const isRegistrationActive =
    statusWorkflowStateKey === "active" ||
    statusWorkflowStateKey === "registered" ||
    (!statusWorkflowState &&
      (holdingStatusKey === "active" || holdingStatusKey === "registered"));
  const claimsBlocked =
    statusWorkflowStateKey === "draft" || holdingStatusKey === "draft";
  const existingClaim = claimsData?.results?.[0] ?? null;
  const activeSyntheticWarranty =
    !issuedWarranty && isRegistrationActive && !hasStatusWorkflowIssues;
  const registeredProductName =
    holding.product.cached_short_name ||
    holding.product.cached_long_name ||
    "Registered product";
  const parentProduct = holding.product.parent_product;
  const productName =
    parentProduct?.cached_short_name ||
    parentProduct?.cached_long_name ||
    registeredProductName;
  const variantName = registeredProductName;
  const rawWarrantyType =
    WARRANTY_TYPE_LABELS[warranty.warranty_type] ||
    humanise(warranty.warranty_type) ||
    "Warranty";
  const warrantyType = activeSyntheticWarranty
    ? warranty.class_name || "Warranty"
    : rawWarrantyType;
  const warrantyCategoryName = warranty.class_name || warrantyType;
  const hostCardTitle =
    hostEntity?.label ||
    holding.host?.host_template?.name?.trim() ||
    "Registration";
  const HostIcon =
    ICON_MAP[hostEntity?.icon ?? ""] || ICON_MAP[DEFAULT_ICON_KEY];
  const ownerName =
    [holding.consumer_account.first_name, holding.consumer_account.last_name]
      .filter(Boolean)
      .join(" ") || "Customer";
  const activeFrom = warranty.active_from ?? holding.created_at;
  const expiresAt = computeWarrantyExpiresAt(warranty);
  const reference = holding.sales_reference || formatHoldingId(holding.uuid);
  const host = holding.host;
  const hostMake = hostTemplateAttrText(host, "make");
  const hostModel = hostTemplateAttrText(host, "model");
  const hostMakeModelTitle = [hostMake, hostModel]
    .filter(Boolean)
    .join(" ")
    .trim();
  const vehicleViewTitle =
    hostMakeModelTitle ||
    host?.name?.trim() ||
    host?.registration_number?.trim() ||
    productName;
  const hostSeedKind = host?.host_template?.seed_kind?.toUpperCase() ?? "";
  const hostKindRaw = (host?.kind ?? "").toUpperCase();
  const isVesselHost = hostSeedKind === "VESSEL" || hostKindRaw === "VESSEL";
  const headerVinRaw = (host?.vin ?? "").trim();
  const headerVinSubtitle =
    headerVinRaw.length > 0
      ? `${isVesselHost ? "HIN" : "VIN"} ${headerVinRaw}`
      : "";
  const headerRegistrationSubtitle = (host?.registration_number ?? "").trim();
  // The REG id now leads the record title, so it is omitted from the meta line.
  // Empty
  // segments (e.g. no VIN for a host-less product) are dropped rather than
  // rendered as a stray "-".
  const vehicleViewMetaLine = [
    ownerName,
    headerVinSubtitle,
    headerWarrantyTemplateLabel(warranty),
  ]
    .filter((segment) => segment && segment !== "-")
    .join(" · ");
  const recordStatus = activeSyntheticWarranty
    ? deriveRecordStatus(holding.status, "Registered")
    : deriveRecordStatus(
        holding.status,
        statusWorkflowState?.state_label || warranty.status,
      );
  const hasWorkflowIssues = pendingIssues.length > 0;
  // WPS-180: the copy below used to assert "Standard warranty is already
  // active" for ANY registration with an open workflow issue, so an unpaid
  // Platinum roof was told it was on Standard.
  //
  // Defined by the NEGATIVE, and deliberately so. The decision this copy
  // describes is "grant the extended tier or stay as you are", so the only
  // thing that needs identifying is whether extended cover is already held.
  //
  // Matching `class_name === "standard"` only ever worked for a tenant who
  // names their base tier "Standard". Matching `=== "MANUFACTURER_WARRANTY"`
  // is no better: 12 Assured issues no manufacturer warranty at all - their
  // base tier is a RETAILER_WARRANTY - so every one of their registrations
  // would fall to the extended-cover copy. Anything that is not EXTENDED is
  // the cover a registration holds before an upgrade is granted, whatever
  // that tenant calls it and whichever type they issue it as.
  // Kept identical to the sibling page, which renders the same copy.
  const hasWorkflowAttached = (holding.workflow_states?.length ?? 0) > 0;
  const needsAction =
    !activeSyntheticWarranty &&
    (hasWorkflowIssues ||
      ["draft", "pending", "needs_action"].includes(
        (warranty.status || holding.status || "").toLowerCase(),
      ));
  const handleDownloadCertificate = async () => {
    if (!orgUuid) {
      toast.error("Organisation context is unavailable.");
      return;
    }

    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Allow pop-ups to download the certificate.");
      return;
    }

    win.document.write(
      '<!doctype html><html><body style="font-family:sans-serif;padding:24px">Preparing certificate...</body></html>',
    );
    win.document.close();

    setIsDownloadingCertificate(true);
    try {
      const res = await api.post(
        `/organisations/${orgUuid}/certificate-templates/preview/`,
        {
          template_slug: "registration_certificate",
          holding_uuid: holding.uuid,
        },
      );
      const html =
        res.data?.data?.html ?? res.data?.result?.html ?? res.data?.html;
      if (!html) {
        toast.error("Certificate template not found");
        win.close();
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 600);
    } catch (err) {
      win.close();
      toastApiError(err, "Failed to generate certificate");
    } finally {
      setIsDownloadingCertificate(false);
    }
  };
  const purchaseAmount = holding.purchase_price
    ? formatCurrency(Number(holding.purchase_price))
    : "-";
  const configuredCustomerRows = buildRegistrationConfiguredFieldItems({
    holding,
    columns: registrationCardDisplayColumns(consumerEntity),
    entityTypes,
    statusWorkflowKey: STATUS_WORKFLOW_KEY,
    locale,
    currency,
  });
  const configuredEntityRows = buildRegistrationConfiguredFieldItems({
    holding,
    columns: registrationCardDisplayColumns(hostEntity),
    entityTypes,
    statusWorkflowKey: STATUS_WORKFLOW_KEY,
    locale,
    currency,
  });
  const productCardColumns = registrationCardDisplayColumns(holdingEntity)
    .filter((column) => !column.id.startsWith("transaction_attribute:"))
    // Scope template-projected HOLDING captures (e.g. the roofing installed-
    // component questions) to holdings on that template, so a non-roofing
    // record's Product card does not render them as empty rows (WPS-149).
    .filter((column) =>
      holdingRegistrationCardColumnApplies(column, holding, entityTypes),
    );
  const configuredProductRows = buildRegistrationConfiguredFieldItems({
    holding,
    columns: productCardColumns,
    entityTypes,
    statusWorkflowKey: STATUS_WORKFLOW_KEY,
    locale,
    currency,
  });
  // Product template's own attributes (from ProductSummarySerializer), filtered
  // to non-blank so an attribute that was never captured is not shown as a
  // blank "-" row ("if not collected, don't display"). Rendered ONLY when the
  // tenant authored no product card display config (either-or, not additive):
  // a configured product card takes priority and shows its columns alone,
  // mirroring the purchase card. Tenant-agnostic.
  const productTemplateAttrRows: RegistrationConfiguredFieldItem[] = (
    holding.product.attribute_values ?? []
  ).flatMap((attr) => {
    if (isDisplayEmpty(attr.value)) return [];
    const field = defineField({
      code_name: attr.code_name,
      display_name: attr.display_name,
      field_type: (attr.field_type ?? "TEXT") as FieldType,
    });
    return [
      {
        key: attr.code_name,
        label: attr.display_name,
        value: attr.value,
        displayValue: formatDisplayValue(field, attr.value, {
          locale,
          currency,
        }),
        muted: false,
      },
    ];
  });
  // Columns for the purchase / transaction card, gated by the fields the
  // registration step actually collected (see purchaseCardDisplayColumns).
  const purchaseCardColumns = purchaseCardDisplayColumns(
    holdingEntity,
    holding.collected_transaction_attribute_codes,
  );
  const configuredPurchaseRows = buildRegistrationConfiguredFieldItems({
    holding,
    columns: purchaseCardColumns,
    entityTypes,
    statusWorkflowKey: STATUS_WORKFLOW_KEY,
    locale,
    currency,
  });
  // The warranty entity's registration_card columns are tenant-wide - every
  // warranty EAV attribute across every template. Filter to non-empty
  // (`!item.muted`) so a registered warranty's card shows only the attributes
  // it actually carries, not other templates' attributes as blank "-" rows
  // (a solar "Standard" warranty was showing OC's roofing attributes). Same
  // convention as the success card (step-review.tsx); tenant-agnostic.
  const configuredWarrantyRows = buildRegistrationConfiguredFieldItems({
    holding,
    columns: registrationCardDisplayColumns(warrantyEntity),
    entityTypes,
    statusWorkflowKey: STATUS_WORKFLOW_KEY,
    locale,
    currency,
  }).filter((item) => !item.muted);
  const customerEmail = holding.consumer_account.email || "";
  return (
    <AppShell>
      <main className="min-h-full bg-[var(--bg)] pb-24">
        <div className="border-b-[0.5px] border-[var(--rule-light)]">
          <div className="page-container flex h-12 items-center gap-2 overflow-hidden text-[11.5px] tracking-[0.04em] text-[var(--ink-3)]">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/registrations")}
              className="h-auto shrink-0 gap-1 bg-transparent p-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to registrations
            </Button>
          </div>
        </div>

        <div className="page-container py-8">
          <section className="flex items-start justify-between gap-8">
            <div className="min-w-0">
              {/*<div className="flex flex-wrap items-center gap-2 mb-3 ">
                <span className="text-[10.5px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
                  Extended warranty review
                </span>
                {hasWorkflowIssues && (
                  <Badge
                    variant="outline"
                    className="h-auto border-[var(--gold-border)] bg-[var(--gold-bg)] px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-[var(--gold-ink)]"
                  >
                    Needs your judgement
                  </Badge>
                )}
                </div>*/}
              {/* Cancelled stays in the list and keeps its record, so say so at
                  the top rather than only in a signal the page hides when no
                  workflow is attached. */}
              {holding.status === "cancelled" && (
                <Badge
                  data-testid="registration-cancelled-badge"
                  className="mb-3 h-auto rounded-[4px] bg-[var(--status-error-bg)] px-2.5 py-1 text-[10px] font-normal uppercase tracking-[0.08em] text-[var(--status-error)]"
                >
                  {t("registrationDetail.cancelled", "Cancelled")}
                </Badge>
              )}
              <div className="flex items-start gap-2">
                <h1 className="max-w-[760px] font-display text-[42px] font-light leading-[1.12] tracking-[-0.01em] text-[var(--ink)]">
                  {formatHoldingId(holding.uuid)} - {vehicleViewTitle}
                </h1>
              </div>
              {headerRegistrationSubtitle && (
                <div className="mt-2 text-[14px] font-light text-[var(--ink-2)]">
                  {headerRegistrationSubtitle}
                </div>
              )}
              {hasWorkflowIssues && (
                <p className="mt-2 max-w-[760px] text-[13px] font-light leading-[1.55] text-[var(--ink-3)]">
                  {t(
                    "registrations.coverBlockersUnresolved",
                    "This registration holds {{cover}} cover. The blockers below are unresolved.",
                    { cover: warrantyCategoryName },
                  )}
                </p>
              )}
              <div className="mt-4 font-mono text-[11.5px] text-[var(--ink-3)]">
                {vehicleViewMetaLine}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                onClick={() =>
                  existingClaim
                    ? navigate(`/claims/${existingClaim.uuid}`)
                    : navigate(`/claims/submit?holding=${holding.uuid}`)
                }
                disabled={!existingClaim && claimsBlocked}
                title={
                  !existingClaim && claimsBlocked
                    ? "This registration is still pending approval - claims are blocked until it is activated."
                    : undefined
                }
                className="cursor-pointer rounded-[4px] border-none bg-[var(--green)] px-3.5 py-2.5 text-[11px] font-normal uppercase tracking-[0.1em] text-white hover:bg-[var(--green-hover)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {existingClaim ? "View Claim" : "+ Claim"}
              </Button>
              <ConfigHelp
                sections={[
                  "display.product_card",
                  "display.customer_card",
                  "display.purchase_card",
                  "display.warranty_card",
                ]}
                returnLabel="Warranty record"
                params={{ warrantyTemplateUuid }}
                size="icon-lg"
                className="border-[0.5px] border-[var(--rule-strong)] hover:bg-[var(--bg-subtle)]"
              />
            </div>
          </section>

          {hasWorkflowAttached && (
            <section className="mt-7 grid overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] shadow-[0_12px_34px_rgba(26,26,24,0.04)] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <RecordSignal
                label="Status"
                value={<StatusBadge status={recordStatus} />}
                sub={
                  holding.status === "cancelled" ||
                  holding.status === "transferred"
                    ? "Registration status"
                    : "Current"
                }
              />
              <RecordSignal
                label="Class"
                value={
                  tiersEnabled ? (
                    <TierBadge
                      name={warrantyCategoryName}
                      finish={warranty.class_finish}
                      size="badge"
                    />
                  ) : (
                    <SignalBadge tone="neutral">
                      {warrantyCategoryName}
                    </SignalBadge>
                  )
                }
                sub={warrantyType}
              />
              <RecordSignal
                label="Term"
                value={
                  <SignalBadge tone="gold">
                    {durationLabel(warranty)}
                  </SignalBadge>
                }
                sub="Coverage"
              />
              <RecordSignal
                label="Active from"
                value={activeFrom ? formatDate(activeFrom) || activeFrom : "-"}
                sub="Start date"
              />
              <RecordSignal
                label="End date"
                value={
                  warranty.is_lifetime ? (
                    <SignalBadge tone="gold">Lifetime</SignalBadge>
                  ) : expiresAt ? (
                    formatDate(expiresAt) || expiresAt
                  ) : (
                    "-"
                  )
                }
                sub={warranty.is_lifetime ? "No expiry" : "Expiry date"}
              />
            </section>
          )}

          {needsAction && hasWorkflowIssues && (
            <section className="mt-7">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="m-0 flex items-center gap-1.5 text-[11px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
                  <ShieldAlert className="h-3 w-3" />
                  Rule evaluation · {pendingIssues.length} blocker
                  {pendingIssues.length === 1 ? "" : "s"}
                </h2>
              </div>
              <div className="space-y-2">
                {pendingIssues.map((issue) => (
                  <div
                    key={issue.uuid}
                    className="flex items-center justify-between gap-4 rounded-[4px] bg-[var(--bg-subtle)] px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[12.5px] font-medium text-[var(--ink)]">
                        {issue.rule_name || issue.message}
                      </div>
                      <div className="mt-0.5 truncate text-[11.5px] text-[var(--ink-3)]">
                        {issue.message}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        openModal(WorkflowIssueResolveModal, {
                          uuid: issue.uuid,
                        })
                      }
                      className="h-7 shrink-0 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] px-3 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--ink-2)] hover:bg-[var(--bg)] hover:text-[var(--green)]"
                    >
                      Apply exception
                    </Button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="mb-2 mt-8 border-t-[0.5px] border-[var(--rule)] pt-6">
            <h2 className="m-0 font-display text-[18px] font-medium tracking-[-0.02em] text-[var(--ink)]">
              Registration details
            </h2>
          </div>

          <section className="mb-8 mt-4">
            <PinMyViewBar view={cardsView} />
            <DraggableCardGrid
              storageKey="registration-detail-cards-v1"
              locked={cardsView.pinned}
              className="grid grid-cols-1 gap-6 md:grid-cols-2"
              cards={[
                {
                  key: "customer",
                  title: "Customer",
                  node: (
                    <DetailCard
                      title="Customer"
                      icon={<UserCircle className="h-3.5 w-3.5" />}
                      collapsible={false}
                      trailing={
                        <div className="flex items-center gap-2">
                          <SharedAddressBadge
                            consumerUuid={holding.consumer_account.uuid}
                            holdingUuid={holding.uuid}
                          />
                          <CustomerActionControls
                          consumerUuid={holding.consumer_account.uuid}
                          holdingUuid={holding.uuid}
                          triggerLabel="More customer actions"
                          triggerTestId="registration-customer-menu"
                          editButtonTestId="registration-customer-edit-btn"
                          triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                          onActionComplete={() => refetch()}
                          onNavigate={(action) => {
                            const config = action.handler_config as Record<
                              string,
                              string
                            >;
                            if (config?.action === "copy") {
                              const values: Record<string, string> = {
                                email: holding.consumer_account.email ?? "",
                              };
                              const value = values[config.field] ?? "";
                              if (value) {
                                navigator.clipboard.writeText(value);
                                toast.success("Copied to clipboard");
                              }
                              return;
                            }
                            const url = config?.url;
                            if (!url) return;
                            const resolved = url.replace(
                              /\{\{(\w+)\}\}/g,
                              (_: string, token: string) =>
                                ({
                                  uuid: holding.consumer_account.uuid,
                                  email: holding.consumer_account.email ?? "",
                                })[token] ?? "",
                            );
                            // External URLs (mailto:, tel:, http(s)://) go through the
                            // browser; only in-app paths use the SPA router.
                            if (/^(mailto:|tel:|https?:\/\/)/.test(resolved))
                              window.location.href = resolved;
                            else navigate(resolved);
                          }}
                        />
                        </div>
                      }
                    >
                      {configuredCustomerRows.length > 0 ? (
                        <RegistrationConfiguredFieldRows
                          items={configuredCustomerRows}
                        />
                      ) : (
                        <>
                          <FieldRow
                            label="First name"
                            value={
                              holding.consumer_account.first_name || undefined
                            }
                          />
                          <FieldRow
                            label="Last name"
                            value={
                              holding.consumer_account.last_name || undefined
                            }
                          />
                          <FieldRow
                            label="Email"
                            value={holding.consumer_account.email || undefined}
                            link={Boolean(customerEmail)}
                            onLinkClick={
                              customerEmail
                                ? () => {
                                    window.location.href = `mailto:${customerEmail}`;
                                  }
                                : undefined
                            }
                          />
                          <FieldRow
                            label="Phone"
                            value={
                              formatPhoneDisplay(
                                holding.consumer_account.phone_number,
                              ) || undefined
                            }
                          />
                          {/* Retain the legacy mailing address in the fallback layout
                    (no configured consumer columns) until the composite ADDRESS
                    field lands via SX-128. */}
                          <FieldRow
                            label="Mailing"
                            value={
                              holding.mailing_address?.display ?? undefined
                            }
                          />
                        </>
                      )}
                      <FieldRow
                        label="Profile"
                        value="View customer"
                        link
                        onLinkClick={() =>
                          navigate(
                            `/customers/${holding.consumer_account.uuid}`,
                          )
                        }
                      />
                    </DetailCard>
                  ),
                },
                {
                  key: "product",
                  title: "Product",
                  node: (
                    <DetailCard
                      title="Product"
                      icon={<Package className="h-3.5 w-3.5" />}
                      collapsible={false}
                      trailing={
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() =>
                              navigate(
                                `/manage/products/${holding.product.uuid}`,
                              )
                            }
                            variant="outline"
                            size="xs"
                            className="flex cursor-pointer items-center gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] text-[10px] uppercase tracking-[0.06em] text-[var(--ink)] transition-all duration-150 hover:border-[var(--green)] hover:bg-[var(--green-bg)]"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View product
                          </Button>
                          {/* Product actions are holding-scoped: re-pointing a
                      registration to the correct product/variant writes
                      holding.product on THIS holding (resourceUuid below is the
                      holding, not the product, so a shared product never
                      re-points the wrong registration). Config-driven and
                      inert for tenants with no holding actions - the menu
                      renders nothing when the actions list is empty. */}
                          <EntityActionMenu
                            entityTypeKey="holding"
                            resourceUuid={holding.uuid}
                            triggerLabel="More product actions"
                            triggerTestId="registration-product-menu"
                            triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                            contentClassName="min-w-[210px]"
                            // The partner change lives on the partner card.
                            excludeHandlerKeys={["change_partnership"]}
                            onActionComplete={() => refetch()}
                          />
                        </div>
                      }
                    >
                      {configuredProductRows.length > 0 ? (
                        // Card display config takes priority: render the configured
                        // columns alone (either-or, not additive).
                        <RegistrationConfiguredFieldRows
                          items={configuredProductRows}
                        />
                      ) : (
                        // No product card display config: fall back to the product
                        // identity plus the product template's own attributes
                        // (non-blank only), so a fresh tenant still sees them.
                        <>
                          <FieldRow label="Product" value={productName} />
                          {/* Variant axis (e.g. colour) - only when the variant
                      value is genuinely distinct from the product name.
                      Standalone products (and any case where the variant
                      would just repeat the product) omit the row entirely
                      instead of echoing the product name under "Color". */}
                          {variantName && variantName !== productName ? (
                            <FieldRow label="Color" value={variantName} />
                          ) : null}
                          <FieldRow
                            label="Category"
                            value={holding.product_category || undefined}
                          />
                          {productTemplateAttrRows.length > 0 && (
                            <RegistrationConfiguredFieldRows
                              items={productTemplateAttrRows}
                            />
                          )}
                        </>
                      )}
                      <FieldRow
                        label="View"
                        value="View product"
                        link
                        onLinkClick={() =>
                          navigate(`/manage/products/${holding.product.uuid}`)
                        }
                      />
                    </DetailCard>
                  ),
                },
                {
                  key: "warranty",
                  title: t("registrations.warrantyCardWithType", "Warranty · {{type}}", {
                    type: warrantyType,
                  }),
                  node: (
                    <DetailCard
                      title={t("registrations.warrantyCardWithType", "Warranty · {{type}}", {
                    type: warrantyType,
                  })}
                      icon={<Shield className="h-3.5 w-3.5" />}
                      collapsible={false}
                      trailing={
                        <div className="flex items-center gap-1.5">
                          {certificateDownloadEnabled && (
                            <Button
                              onClick={handleDownloadCertificate}
                              variant="outline"
                              size="xs"
                              disabled={!orgUuid || isDownloadingCertificate}
                              className="flex cursor-pointer items-center gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] text-[10px] uppercase tracking-[0.06em] text-[var(--ink)] transition-all duration-150 hover:border-[var(--green)] hover:bg-[var(--green-bg)] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Printer className="h-3 w-3" />
                              {isDownloadingCertificate
                                ? t(
                                    "registrationDetail.certificatePreparing",
                                    "Preparing",
                                  )
                                : t(
                                    "registrationDetail.certificate",
                                    "Certificate",
                                  )}
                            </Button>
                          )}
                          {/* Config-driven warranty actions (e.g. Cancel warranty) come
                      from the tenant's EntityAction catalog on the "warranty"
                      entity type. Renders nothing when the tenant has no
                      warranty actions, so the menu is inert for those tenants. */}
                          {issuedWarranty && (
                            <EntityActionMenu
                              entityTypeKey="warranty"
                              resourceUuid={issuedWarranty.uuid}
                              triggerLabel={t(
                                "registrationDetail.moreWarrantyActions",
                                "More warranty actions",
                              )}
                              triggerTestId="registration-warranty-menu"
                              triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                              contentClassName="min-w-[240px]"
                              // The certificate button beside this menu already exposes
                              // the print action, so hide the duplicate menu item.
                              excludeActionKeys={["print_certificate"]}
                              onActionComplete={() => refetch()}
                            />
                          )}
                        </div>
                      }
                    >
                      {configuredWarrantyRows.length > 0 ? (
                        <RegistrationConfiguredFieldRows
                          items={configuredWarrantyRows}
                          tierFinish={
                            tiersEnabled ? warranty.class_finish : undefined
                          }
                        />
                      ) : (
                        <>
                          <FieldRow
                            label="Status"
                            value={<StatusBadge status={recordStatus} />}
                          />
                          <FieldRow
                            label="Class"
                            value={
                              tiersEnabled ? (
                                <TierBadge
                                  name={warrantyCategoryName}
                                  finish={warranty.class_finish}
                                />
                              ) : (
                                warrantyCategoryName
                              )
                            }
                          />
                          <FieldRow
                            label="Duration"
                            value={durationLabel(warranty)}
                          />
                          <FieldRow
                            label="Active from"
                            value={
                              activeFrom
                                ? formatDate(activeFrom) || activeFrom
                                : undefined
                            }
                          />
                          {!warranty.is_lifetime && (
                            <FieldRow
                              label="Expires"
                              value={
                                expiresAt
                                  ? formatDate(expiresAt) || expiresAt
                                  : undefined
                              }
                            />
                          )}
                        </>
                      )}
                      {(issuedWarranty?.documents ?? []).length > 0 && (
                        <div className="border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-[9px]">
                          <div className="mb-2 text-[9.5px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
                            Documents
                          </div>
                          {issuedWarranty?.documents?.map((document) =>
                            document.url ? (
                              <a
                                key={document.uuid}
                                href={document.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 flex items-center justify-between gap-3 text-[12px] hover:underline"
                              >
                                <span className="flex min-w-0 items-center gap-1.5 text-[var(--green)]">
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                  <span className="truncate">
                                    {document.label}
                                  </span>
                                </span>
                                <span className="font-mono text-[10px] text-[var(--ink-3)]">
                                  {document.kind}
                                </span>
                              </a>
                            ) : (
                              <div
                                key={document.uuid}
                                className="mt-1 flex items-center justify-between gap-3 text-[12px]"
                              >
                                <span className="truncate text-[var(--ink-3)]">
                                  {document.label}
                                </span>
                                <span className="font-mono text-[10px] text-[var(--ink-3)]">
                                  {document.kind}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                    </DetailCard>
                  ),
                },
                // Host card: only when a host is linked. Host-less Standard SaaS
                // tenants (e.g. Teknor) get no Host card by default - it is additive
                // and arrives with the host record.
                holding.host
                  ? {
                      key: "host",
                      title: hostCardTitle,
                      node: (
                        <DetailCard
                          title={hostCardTitle}
                          icon={<HostIcon className="h-3.5 w-3.5" />}
                          collapsible={false}
                          trailing={
                            holding.host?.uuid ? (
                              // The card the record is asked to correct most
                              // often - a mis-typed plate, a wrong door number -
                              // and the only core card that carried no actions
                              // at all. The menu renders nothing where a tenant
                              // has no host actions, so this is inert until the
                              // default lands.
                              <EntityActionMenu
                                entityTypeKey="host"
                                resourceUuid={holding.host.uuid}
                                triggerLabel={t(
                                  'registrations.moreHostActions',
                                  'More {{noun}} actions',
                                  { noun: hostCardTitle.toLowerCase() },
                                )}
                                triggerTestId="registration-host-menu"
                                triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                                contentClassName="min-w-[210px]"
                                onActionComplete={() => refetch()}
                              />
                            ) : undefined
                          }
                        >
                          {configuredEntityRows.length > 0 ? (
                            <RegistrationConfiguredFieldRows
                              items={configuredEntityRows}
                            />
                          ) : (
                            <>
                              {(hostEntity?.attributes ?? [])
                                .filter(
                                  (attr) =>
                                    attr.configuration_data?.display?.detail !==
                                    false,
                                )
                                .map((attr) => {
                                  const raw = hostTemplateAttrText(
                                    holding.host,
                                    attr.code_name,
                                  );
                                  const value =
                                    raw ||
                                    (
                                      holding.host as
                                        Record<string, unknown> | undefined
                                    )?.[attr.code_name];
                                  const display =
                                    value != null && value !== ""
                                      ? (typeof value === "object"
                                          ? formatEntityCardValue(value, {
                                              locale,
                                              currency,
                                            })
                                          : humanise(value)) || undefined
                                      : undefined;
                                  return (
                                    <FieldRow
                                      key={attr.code_name}
                                      label={attr.display_name}
                                      value={display}
                                    />
                                  );
                                })}
                            </>
                          )}
                        </DetailCard>
                      ),
                    }
                  : null,
                // Assigned contractor / installer. The registration keeps the
                // partner it was registered against visible on the record, not just
                // in the wizard (WPS-163). Title comes from the tenant's external
                // role name, and the panel renders whatever partner attributes the
                // tenant defines, so this serves any partner role. Hidden entirely
                // when no partner is attached.
                assignedPartner
                  ? {
                      key: "partner",
                      title: assignedPartner.role_name,
                      node: (
                        <DetailCard
                          title={assignedPartner.role_name}
                          icon={<HardHat className="h-3.5 w-3.5" />}
                          collapsible={false}
                          trailing={
                            <EntityActionMenu
                              entityTypeKey="holding"
                              resourceUuid={holding.uuid}
                              triggerLabel={t(
                                "registrationDetail.morePartnerActions",
                                "More {{partner}} actions",
                                { partner: assignedPartner.role_name },
                              )}
                              triggerTestId="registration-partner-menu"
                              triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                              contentClassName="min-w-[210px]"
                              onlyHandlerKeys={["change_partnership"]}
                              onActionComplete={() => refetch()}
                            />
                          }
                        >
                          <PartnerProfilePanel partnership={assignedPartner} />
                        </DetailCard>
                      ),
                    }
                  : null,
                {
                  key: "purchase",
                  title: "Purchase",
                  node: (
                    <DetailCard
                      title="Purchase"
                      icon={<CreditCard className="h-3.5 w-3.5" />}
                      collapsible={false}
                      // The purchase card shows the dealer, so admins change
                      // (or assign) it here too, partner attached or not.
                      trailing={
                        <EntityActionMenu
                          entityTypeKey="holding"
                          resourceUuid={holding.uuid}
                          triggerLabel={t(
                            "registrationDetail.morePurchaseActions",
                            "More purchase actions",
                          )}
                          triggerTestId="registration-purchase-menu"
                          triggerClassName="h-7 w-7 rounded-[4px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
                          contentClassName="min-w-[210px]"
                          onlyHandlerKeys={["change_partnership"]}
                          onActionComplete={() => refetch()}
                        />
                      }
                    >
                      {configuredPurchaseRows.length > 0 ? (
                        <RegistrationConfiguredFieldRows
                          items={configuredPurchaseRows}
                        />
                      ) : (
                        <>
                          <FieldRow
                            label="Date"
                            value={
                              activeFrom
                                ? formatDate(activeFrom) || activeFrom
                                : undefined
                            }
                          />
                          <FieldRow label="Price" value={purchaseAmount} />
                          <FieldRow
                            label="Sales person"
                            value={holding.sales_person || undefined}
                          />
                          <FieldRow label="Reference" value={reference} />
                          <FieldRow label="Owner" value={ownerName} />
                        </>
                      )}
                    </DetailCard>
                  ),
                },
                {
                  key: "parts",
                  title: "Parts on unit",
                  node: <PartsOnUnitCard links={holding.part_links ?? []} />,
                },
                {
                  key: "notes",
                  title: "Notes",
                  node: (
                    <NotesPanel
                      recordId={holding.uuid}
                      recordType="registration"
                      canAddInternal={true}
                      canAddCustomerVisible={true}
                      allowAttachments
                    />
                  ),
                },
                // Activity log: the last cards on the record - notes then the
                // append-only audit trail of edits, status changes and
                // correspondence on the holding (mirrors the OC property record's
                // activity view). Lives in the same grid so it fills the empty slot
                // rather than spanning a new full-width row.
                {
                  key: "activity",
                  title: "Activity log",
                  node: (
                    <div id="workflow-history" className="scroll-mt-6">
                      {/* The tier / terms actions on the warranty card above run
                  against the "warranty" entity, so their audit entries are
                  written under that entity key - include it here or an
                  upgrade / downgrade leaves no visible trace (WPS-180). */}
                      <EntityHistoryCard
                        // "warranty" included because warranty actions (cancel, void,
                        // and WPS-180's tier upgrade / downgrade) log against the
                        // warranty, not the holding - without it the action was
                        // recorded and the card still read as empty.
                        entityKeys={["holding", "host", "consumer", "warranty"]}
                        entityIds={[
                          holding.uuid,
                          holding.host?.uuid,
                          holding.consumer_account?.uuid,
                          // Every warranty on the holding, plus the one this page is
                          // showing. That is normally one of them, but naming it
                          // explicitly means a record reached directly by uuid still
                          // resolves. Deduped here because the card joins these into a
                          // single query param without deduping.
                          ...new Set(
                            [
                              ...(holding.warranties ?? []).map((w) => w.uuid),
                              issuedWarranty?.uuid,
                            ].filter((uuid): uuid is string => Boolean(uuid)),
                          ),
                        ]}
                        title="Activity log"
                      />
                    </div>
                  ),
                },
              ].filter((c): c is ReorderableCard => Boolean(c))}
            />
          </section>
        </div>
      </main>
    </AppShell>
  );
}

function RecordSignal({
  label,
  value,
  sub,
  icon,
}: {
  label: string;
  value: ReactNode;
  sub: string;
  icon?: ReactNode;
}) {
  return (
    <div className="min-w-0 border-b-[0.5px] border-b-[var(--rule-light)] px-5 py-4 last:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0 sm:[&:nth-child(odd)]:border-r-[0.5px] sm:[&:nth-child(odd)]:border-r-[var(--rule-light)] lg:[&:nth-child(odd)]:border-r-0 xl:border-b-0 xl:border-r-[0.5px] xl:border-r-[var(--rule-light)] xl:last:border-r-0">
      <div className="mb-2 flex items-center gap-1.5 text-[9.5px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {icon ? <span className="text-[var(--ink-3)]">{icon}</span> : null}
        <span>{label}</span>
      </div>
      <div className="flex min-h-7 items-center font-sans text-[14px] font-medium text-[var(--ink)]">
        {value}
      </div>
      <div className="mt-1.5 truncate text-[11.5px] leading-[1.4] text-[var(--ink-3)]">
        {sub}
      </div>
    </div>
  );
}

function SignalBadge({
  children,
  tone,
}: {
  children: ReactNode;
  tone: "success" | "warning" | "gold" | "neutral";
}) {
  const toneClassName =
    tone === "success"
      ? "border-transparent bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
      : tone === "warning"
        ? "border-transparent bg-[var(--bg-subtle)] text-[var(--ink-2)]"
        : tone === "gold"
          ? "border-transparent bg-[var(--gold-bg-muted)] text-[var(--gold-ink)]"
          : "border-transparent bg-[var(--bg-subtle)] text-[var(--ink-2)]";

  return (
    <Badge
      variant="outline"
      className={[
        "h-[24px] max-w-full rounded-[3px] px-2.5 text-[10px] font-normal uppercase tracking-[0.08em] shadow-none",
        toneClassName,
      ].join(" ")}
    >
      <span className="truncate">{children}</span>
    </Badge>
  );
}
